import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { activeTasksQueryOptions, taskProgressKeys } from "@/lib/queries/task-progress.queries";
import { opportunitySetKeys } from "@/lib/queries/opportunity-sets.queries";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useAuth } from "@/features/auth/hooks/useAuth";

/** Prefix matching every tenant's opportunity-sets list cache. */
const SETS_LIST_PREFIX = [...opportunitySetKeys.all, "list"] as const;

/**
 * Cancels the in-flight opportunity-set generation for a given setId by mapping it
 * to its task-progress correlationId (via the active-tasks index) and running the
 * SAME steps as the task-progress sidebar's Stop button: POST /tasks/:id/cancel
 * (worker aborts + soft-deletes the set + frees the lock) then removes the task card.
 *
 * Returns `stop(setId, onCancelled?)` → true if a cancellable task was found and
 * cancellation was requested, false otherwise (e.g. the task already finished/expired).
 * `onCancelled` runs only after cancel + dismiss succeed, so callers can hide the
 * task card without racing a failed request.
 */
export function useStopOpportunityGeneration(): (
  setId: string,
  onCancelled?: (correlationId: string) => void,
) => boolean {
  const userEmail = useAuth((s) => s.user?.email);
  const queryClient = useQueryClient();

  // Shares the active-tasks query cache with the sidebar (same key) — no extra polling.
  const { data: activeTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));

  const cancelMutation = useMutation({
    // Sequential: cancel first (signals worker + frees lock), then remove the task
    // record so the server no longer returns it. By the time onSuccess fires, the task
    // is definitively gone — making any invalidation race-free.
    mutationFn: async ({ correlationId }: { correlationId: string; setId: string }) => {
      await cancelTask(correlationId);
      await dismissTask(correlationId).catch(() => {});
    },
    onMutate: ({ setId }) => {
      // Optimistically drop the generating set from the list immediately.
      // Do NOT touch the task-progress cache here — the sidebar's dismissedIds handles
      // the task card. Removing from cache here would trigger the pruning effect (which
      // clears dismissedIds when a task is absent), and the 1s poll could then bring
      // the task back with no dismissed guard, causing the flash.
      queryClient.setQueriesData<{ id: string }[]>({ queryKey: SETS_LIST_PREFIX }, (prev) =>
        Array.isArray(prev) ? prev.filter((s) => s.id !== setId) : prev,
      );
    },
    onSuccess: () => {
      // Task is now confirmed deleted from the server — safe to invalidate without
      // the refetch returning stale data. Do NOT invalidate the sets list: the
      // optimistic remove from onMutate should stand until the worker's soft-delete
      // is picked up by the natural sets poll (re-invalidating now would fetch the
      // still-GENERATING set and flash the set card back).
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
    },
    onError: () => {
      // Cancel failed — restore both caches from server.
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
      void queryClient.invalidateQueries({ queryKey: SETS_LIST_PREFIX });
    },
  });

  return useCallback(
    (setId: string, onCancelled?: (correlationId: string) => void) => {
      const task = activeTasks.find(
        (t) =>
          t.type === "generate-opportunity-set" &&
          t.cancellable &&
          t.navigation?.params?.setId === setId,
      );
      if (!task) return false;
      cancelMutation.mutate(
        { correlationId: task.correlationId, setId },
        { onSuccess: () => onCancelled?.(task.correlationId) },
      );
      return true;
    },
    [activeTasks, cancelMutation],
  );
}
